import { useState, useMemo, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack, Cancel, CheckCircle, Close, Send } from "@mui/icons-material";

import { useInvoiceDetail, useInvoiceAttachments } from "../api/invoiceQueries";
import { useUpdateInvoice } from "../api/invoiceMutations";
import InvoicePdfViewer from "../components/InvoicePdfViewer";
import StatusBadge from "../components/StatusBadge";
import LoaderV2 from "@/pages/InvoiceManagement/components/LoaderV2";
import { useToast } from "@/pages/InvoiceManagement/hooks/useToast";
import { usePermissions } from "@/context/PermissionsContext";
import { BOUNCER_PERMISSIONS } from "@/constants/bouncerPermissions";
import { useIsMobile } from "@/hooks/useIsMobile";
import StickyActionBar from "@/components/mobile/StickyActionBar";
import { getEffectiveStatus } from "../types/common";
import { getStatusLabel } from "../constants/lifecycleStatus";
import { V2_COLORS } from "../constants/colors";
import { getInvoiceValueDelta } from "../utils/invoiceValueDelta";
import type { V2Invoice, UpdateInvoicePayload } from "../types/invoice";
import { ACTION } from "../types/action";
import type { ValidationCheck, ValidationResult, ValidationFieldResult } from "../types/action";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EditFormState = {
  invoice_no: string;
  invoice_date: string;
  invoice_value: string;
  billing_entity: string;
  bill_to_address: string;
  ship_to_address: string;
  hsn_sac_codes: string;
  additional_charges: string;
};

type FieldValidation = "IGNORE" | boolean;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build initial form state from the invoice API response. */
function buildInitialValues(invoice: V2Invoice): EditFormState {
  return {
    invoice_no: invoice.invoice_no ?? "",
    invoice_date: invoice.invoice_date ? invoice.invoice_date.substring(0, 10) : "",
    invoice_value: invoice.invoice_value != null ? String(invoice.invoice_value) : "",
    billing_entity: invoice.billing_entity ?? "",
    bill_to_address: invoice.bill_to_address ?? "",
    ship_to_address: invoice.ship_to_address ?? "",
    hsn_sac_codes: Array.isArray(invoice.hsn_sac_codes)
      ? invoice.hsn_sac_codes.join(", ")
      : (invoice.hsn_sac_codes ?? ""),
    additional_charges: invoice.additional_charges != null ? String(invoice.additional_charges) : "0",
  };
}

/**
 * Maps a form field name to the key(s) used in validation_result.validation_field_results.
 * The validation result uses parser/PO field names which differ from invoice record field names.
 */
const VALIDATION_FIELD_MAP: Record<string, string[]> = {
  invoice_no: ["invoice_no", "invoice_number"],
  invoice_date: ["invoice_date"],
  invoice_value: ["invoice_value", "invoice_total_value"],
  billing_entity: ["bill_to_name", "billing_entity", "bill_to_entity"],
  bill_to_address: ["bill_to_address"],
  ship_to_address: ["ship_to_address"],
  hsn_sac_codes: ["hsn_code", "hsn_sac_codes"],
};

/** Fields that have validation data and can be locked when green. */
const VALIDATED_FIELDS = ["invoice_value", "billing_entity", "bill_to_address", "ship_to_address", "hsn_sac_codes"] as const;

/** Look up a ValidationFieldResult by trying multiple key names (v1 nested results). */
function findFieldResult(
  results: Record<string, ValidationFieldResult> | undefined,
  keys: string[]
): ValidationFieldResult | undefined {
  if (!results) return undefined;
  for (const key of keys) {
    if (results[key]) return results[key];
  }
  return undefined;
}

/** Look up a ValidationCheck from the flat checks array by trying mapped field names (v2). */
function findCheck(checks: ValidationCheck[], fieldName: string): ValidationCheck | undefined {
  const mappedKeys = VALIDATION_FIELD_MAP[fieldName];
  if (!mappedKeys) return undefined;
  for (const key of mappedKeys) {
    const check = checks.find((c) => c.field === key);
    if (check) return check;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// EditableField sub-component
// ---------------------------------------------------------------------------

type EditableFieldProps = {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: () => void;
  type?: "text" | "date" | "number" | "textarea";
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  validation?: FieldValidation;
  errorMessage?: string;
  helperText?: string;
};

function EditableField({
  label,
  name,
  value,
  onChange,
  onBlur,
  type = "text",
  disabled = false,
  required = false,
  placeholder,
  validation = "IGNORE",
  errorMessage,
  helperText,
}: EditableFieldProps) {
  const showValidation = validation !== "IGNORE";
  const hasFailed = showValidation && validation === false;

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      mb={2.5}
      sx={{ flexDirection: { xs: "column", md: "row" }, gap: { xs: 0.5, md: 1.5 } }}
    >
      {/* Validation indicator + label — beside the input on desktop, above it on mobile */}
      <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: { xs: 0, md: 200 }, pt: { xs: 0, md: 1 } }}>
        {showValidation ? (
          validation ? (
            <CheckCircle sx={{ fontSize: 20, color: V2_COLORS.success }} />
          ) : (
            <Cancel sx={{ fontSize: 20, color: V2_COLORS.error }} />
          )
        ) : (
          <Box width={20} height={20} flexShrink={0} />
        )}
        <Typography variant="body2" fontWeight={500} color="textSecondary">
          {label}
          {required && <span style={{ color: V2_COLORS.error }}> *</span>}
        </Typography>
      </Box>

      {/* Input */}
      <Box flex={1} width="100%">
        <TextField
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          type={type === "textarea" ? undefined : type}
          disabled={disabled}
          placeholder={placeholder}
          fullWidth
          size="small"
          multiline={type === "textarea"}
          minRows={type === "textarea" ? 3 : undefined}
          InputLabelProps={type === "date" ? { shrink: true } : undefined}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
              "& fieldset": {
                borderColor: hasFailed ? V2_COLORS.error : "#CCC",
              },
              "&:hover fieldset": {
                borderColor: hasFailed ? V2_COLORS.error : "#999",
              },
            },
          }}
        />
        {hasFailed && errorMessage && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
            {errorMessage}
          </Typography>
        )}
        {helperText && !hasFailed && (
          <Typography variant="caption" sx={{ mt: 0.5, display: "block", color: V2_COLORS.textSecondary }}>
            {helperText}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function EditInvoice() {
  const { invoiceId: rawId } = useParams<{ invoiceId: string }>();
  const invoiceId = Number(rawId);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = usePermissions();
  const isMobile = useIsMobile();

  // ---- Data fetching ----
  const { data, isLoading, isError } = useInvoiceDetail(invoiceId);
  const { data: attachmentsData } = useInvoiceAttachments(invoiceId);
  const updateMutation = useUpdateInvoice(invoiceId);

  const invoice = data?.invoice;
  const effectiveStatus = invoice ? getEffectiveStatus(invoice) : undefined;
  const validationResult: ValidationResult | undefined = invoice?.validation_result;
  const isV1Invoice = !!invoice && !invoice.lifecycle_status;
  // V2 invoices use the flat checks array from the validation step response
  const checks: ValidationCheck[] = data?.validation_result?.response?.checks ?? [];

  // Primary attachment for PDF viewer
  const primaryAttachment = useMemo(() => {
    if (attachmentsData?.attachments?.length) {
      return attachmentsData.attachments[0];
    }
    // Fallback to invoice_image from detail response
    if (data?.invoice_image?.pre_signed_url) {
      return { url: data.invoice_image.pre_signed_url, name: data.invoice_image.file_name };
    }
    return null;
  }, [attachmentsData, data?.invoice_image]);

  // ---- Form state ----
  const [values, setValues] = useState<EditFormState | null>(null);
  const [initialValues, setInitialValues] = useState<EditFormState | null>(null);
  const [validations, setValidations] = useState<Record<string, FieldValidation>>({});
  const [comments, setComments] = useState("");

  // Initialize form when data loads
  useEffect(() => {
    if (invoice && !initialValues) {
      const iv = buildInitialValues(invoice);
      setValues(iv);
      setInitialValues(iv);
    }
  }, [invoice, initialValues]);

  // ---- Change handler ----
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value: val } = e.target;
      setValues((prev) => (prev ? { ...prev, [name]: val } : prev));
    },
    []
  );

  // ---- Validation on blur ----
  const validateOnBlur = useCallback(
    (fieldName: string) => {
      if (!values || !initialValues) return;

      const currentValue = values[fieldName as keyof EditFormState];
      const initialValue = initialValues[fieldName as keyof EditFormState];

      // If unchanged from initial, keep original validation status
      if (currentValue === initialValue) {
        setValidations((prev) => {
          const next = { ...prev };
          delete next[fieldName];
          return next;
        });
        return;
      }

      // Additional charges has no validation
      if (fieldName === "additional_charges") return;

      // invoice_no and invoice_date: always IGNORE per v1 pattern
      if (fieldName === "invoice_no" || fieldName === "invoice_date") {
        setValidations((prev) => ({ ...prev, [fieldName]: "IGNORE" }));
        return;
      }

      if (!isV1Invoice) {
        // --- V2: compare against system_value from checks ---
        const check = findCheck(checks, fieldName);
        if (!check) return;

        if (fieldName === "invoice_value") {
          const numValue = parseFloat(currentValue);
          const systemNum = parseFloat(String(check.system_value));
          const delta = getInvoiceValueDelta();
          if (!isNaN(systemNum)) {
            setValidations((prev) => ({ ...prev, [fieldName]: Math.abs(numValue - systemNum) <= delta }));
          }
          return;
        }

        const valid = String(currentValue).toLowerCase() === String(check.system_value).toLowerCase();
        setValidations((prev) => ({ ...prev, [fieldName]: valid }));
      } else {
        // --- V1: compare against parser_value and po_value from nested results ---
        if (!validationResult) return;
        const mappedKeys = VALIDATION_FIELD_MAP[fieldName];
        if (!mappedKeys) return;

        const parserField = findFieldResult(validationResult.parser_invoice_result?.validation_field_results, mappedKeys);
        const poField = findFieldResult(validationResult.invoice_po_result?.validation_field_results, mappedKeys);

        if (fieldName === "invoice_value") {
          const numValue = parseFloat(currentValue);
          const delta = getInvoiceValueDelta();
          let valid = true;
          if (parserField?.parser_value != null) {
            const parserNum = parseFloat(String(parserField.parser_value));
            if (!isNaN(parserNum)) valid = valid && Math.abs(numValue - parserNum) <= delta;
          }
          if (poField?.po_value != null) {
            const poNum = parseFloat(String(poField.po_value));
            if (!isNaN(poNum)) valid = valid && Math.abs(numValue - poNum) <= delta;
          }
          setValidations((prev) => ({ ...prev, [fieldName]: valid }));
          return;
        }

        let valid = true;
        if (parserField?.parser_value != null) {
          valid = valid && String(currentValue).toLowerCase() === String(parserField.parser_value).toLowerCase();
        }
        if (poField?.po_value != null) {
          valid = valid && String(currentValue).toLowerCase() === String(poField.po_value).toLowerCase();
        }
        setValidations((prev) => ({ ...prev, [fieldName]: valid }));
      }
    },
    [values, initialValues, isV1Invoice, checks, validationResult]
  );

  /** Get the current validation status for a field. */
  const getValidationStatus = useCallback(
    (fieldName: string): FieldValidation => {
      if (fieldName === "additional_charges") return "IGNORE";
      if (fieldName === "invoice_no" || fieldName === "invoice_date") return "IGNORE";

      // If user has edited and blurred, use their validation result
      if (fieldName in validations) return validations[fieldName];

      if (!isV1Invoice) {
        // V2: use flat checks array
        if (!checks.length) return "IGNORE";
        const check = findCheck(checks, fieldName);
        if (!check) return "IGNORE";
        return check.match;
      } else {
        // V1: use nested parser_invoice_result / invoice_po_result
        if (!validationResult) return "IGNORE";
        const mappedKeys = VALIDATION_FIELD_MAP[fieldName];
        if (!mappedKeys) return "IGNORE";

        const parserField = findFieldResult(
          validationResult.parser_invoice_result?.validation_field_results,
          mappedKeys
        );
        const poField = findFieldResult(
          validationResult.invoice_po_result?.validation_field_results,
          mappedKeys
        );

        if (parserField && poField) return parserField.match_status && poField.match_status;
        if (poField) return poField.match_status;
        if (parserField) return parserField.match_status;

        return "IGNORE";
      }
    },
    [isV1Invoice, checks, validationResult, validations]
  );

  /**
   * Get the backend-only validation status for a field.
   * Used exclusively for the `disabled` gate — a field is locked only when the
   * backend originally validated it as green. User blur corrections never lock
   * the field, so the user can always re-edit a value they just typed.
   */
  const getBackendValidationStatus = useCallback(
    (fieldName: string): FieldValidation => {
      if (fieldName === "additional_charges") return "IGNORE";
      if (fieldName === "invoice_no" || fieldName === "invoice_date") return "IGNORE";

      if (!isV1Invoice) {
        if (!checks.length) return "IGNORE";
        const check = findCheck(checks, fieldName);
        if (!check) return "IGNORE";
        return check.match;
      } else {
        if (!validationResult) return "IGNORE";
        const mappedKeys = VALIDATION_FIELD_MAP[fieldName];
        if (!mappedKeys) return "IGNORE";

        const parserField = findFieldResult(
          validationResult.parser_invoice_result?.validation_field_results,
          mappedKeys
        );
        const poField = findFieldResult(
          validationResult.invoice_po_result?.validation_field_results,
          mappedKeys
        );

        if (parserField && poField) return parserField.match_status && poField.match_status;
        if (poField) return poField.match_status;
        if (parserField) return parserField.match_status;

        return "IGNORE";
      }
    },
    [isV1Invoice, checks, validationResult]
  );

  /** Get the error message for a failed validation. */
  const getErrorMessage = useCallback(
    (fieldName: string): string => {
      if (!isV1Invoice) {
        // V2: use check.reason from flat checks array
        const check = findCheck(checks, fieldName);
        if (!check) return "";

        if (fieldName in validations && validations[fieldName] === false) {
          return check.reason || "Value does not match.";
        }
        if (!check.match) return check.reason || "Value does not match.";
        return "";
      } else {
        // V1: use parser/PO field comparison
        if (!validationResult) return "";
        const mappedKeys = VALIDATION_FIELD_MAP[fieldName];
        if (!mappedKeys) return "";

        const poField = findFieldResult(
          validationResult.invoice_po_result?.validation_field_results,
          mappedKeys
        );
        const parserField = findFieldResult(
          validationResult.parser_invoice_result?.validation_field_results,
          mappedKeys
        );

        if (fieldName in validations && validations[fieldName] === false) {
          if (poField && !parserField) return "Value does not match with the invoice document. Enter manually.";
          return "Value does not match with the PO.";
        }

        if (parserField && !parserField.match_status) {
          return "Value does not match with the invoice document. Enter manually.";
        }
        if (poField && !poField.match_status) {
          return "Value does not match with the PO.";
        }
        return "";
      }
    },
    [isV1Invoice, checks, validationResult, validations]
  );

  // True only when every validated field has a confirmed green check from the backend — nothing for the user to correct.
  const allFieldsValid = useMemo(
    () => VALIDATED_FIELDS.every((f) => getBackendValidationStatus(f) === true),
    [getBackendValidationStatus]
  );

  // ---- Submit ----
  const hasChanges = useMemo(() => {
    if (!values || !initialValues) return false;
    return JSON.stringify(values) !== JSON.stringify(initialValues);
  }, [values, initialValues]);

  const isSaveDisabled = !hasChanges || !comments.trim() || updateMutation.isLoading;

  const handleSubmit = useCallback(() => {
    if (!invoice || !values || !initialValues) return;

    const payload: UpdateInvoicePayload = {
      lifecycle_version: invoice.lifecycle_version,
      comments: comments.trim(),
    };

    // Only include changed fields
    if (values.invoice_no !== initialValues.invoice_no) {
      payload.invoice_number = values.invoice_no;
    }
    if (values.invoice_date !== initialValues.invoice_date) {
      payload.invoice_date = values.invoice_date;
    }
    if (values.invoice_value !== initialValues.invoice_value) {
      const parsedValue = parseFloat(values.invoice_value);
      if (isNaN(parsedValue)) {
        showToast({ type: "error", message: "Invoice value must be a valid number" });
        return;
      }
      payload.invoice_value = parsedValue;
    }
    if (values.billing_entity !== initialValues.billing_entity) {
      payload.billing_entity = values.billing_entity;
    }
    if (values.bill_to_address !== initialValues.bill_to_address) {
      payload.bill_to_address = values.bill_to_address;
    }
    if (values.ship_to_address !== initialValues.ship_to_address) {
      payload.ship_to_address = values.ship_to_address;
    }
    if (values.hsn_sac_codes !== initialValues.hsn_sac_codes) {
      payload.hsn_sac_codes = values.hsn_sac_codes
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    if (values.additional_charges !== initialValues.additional_charges) {
      payload.additional_charges = parseFloat(values.additional_charges) || 0;
    }

    updateMutation.mutate(payload, {
      onSuccess: () => {
        showToast({ type: "success", message: "Invoice updated successfully" });
        navigate(-1);
      },
      onError: (err: unknown) => {
        const message = err instanceof Error ? err.message : "Failed to update invoice";
        showToast({ type: "error", message });
      },
    });
  }, [invoice, values, initialValues, comments, updateMutation, showToast, navigate]);

  // ---- Permission check ----
  const canEdit = hasPermission(BOUNCER_PERMISSIONS.INVOICE_EDIT);
  const isInternal = hasPermission(BOUNCER_PERMISSIONS.ACCOUNTS_PAYABLE_VIEW);

  // ---- Render ----
  if (isLoading) return <LoaderV2 />;

  if (isError || !data || !invoice) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh">
        <Typography variant="h6" color="error">
          Error loading invoice details
        </Typography>
      </Box>
    );
  }

  if (!canEdit) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh" gap={2}>
        <Typography variant="h6" color="textSecondary">
          You do not have permission to edit invoices
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ textTransform: "none" }}>
          Go back
        </Button>
      </Box>
    );
  }

  if (!data?.allowed_actions?.includes(ACTION.EDIT)) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh" gap={2}>
        <Typography variant="h6" color="textSecondary">
          This invoice cannot be edited
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ textTransform: "none" }}>
          Go back
        </Button>
      </Box>
    );
  }

  if (!values) return <LoaderV2 />;

  const actionButtons = (
    <>
      <Button
        variant="outlined"
        startIcon={<Close />}
        onClick={() => navigate(-1)}
        sx={{
          textTransform: "none",
          color: V2_COLORS.textSecondary,
          borderColor: "#CCC",
          "&:hover": { borderColor: "#999", backgroundColor: "#F5F5F5" },
        }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        startIcon={
          updateMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <Send />
        }
        disabled={isSaveDisabled}
        onClick={handleSubmit}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          backgroundColor: V2_COLORS.primary,
          "&:hover": { backgroundColor: V2_COLORS.primaryHover },
          boxShadow: "none",
        }}
      >
        Save
      </Button>
    </>
  );

  return (
    // Mobile: the page is its own scroll region (header + Cancel/Save bar pinned, form scrolls).
    // Same pattern as InvoiceDetail — the Dashboard workspace has overflow:hidden.
    <Box
      sx={
        isMobile
          ? { display: "flex", flexDirection: "column", height: "calc(100vh - 48px)" }
          : { px: 3, pb: "140px" }
      }
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={isMobile ? 2 : 3}
        sx={{
          bgcolor: "background.paper",
          px: isMobile ? 1.5 : 2.5,
          py: 1,
          mx: isMobile ? 0 : -3,
          flexShrink: 0,
          boxShadow: "0px 0px 2px 0px rgba(0,0,0,0.2)",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="body2" color="textSecondary" display="block">
              Edit Invoice
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontSize="16px" fontWeight={600} lineHeight="24px">
                {invoice.invoice_no}
              </Typography>
              {effectiveStatus && (
                <StatusBadge
                  status={effectiveStatus}
                  displayLabel={!invoice.lifecycle_status ? data.invoice_status?.label : getStatusLabel(effectiveStatus, isInternal, data.po_source)}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={
          isMobile
            ? { px: 2, pb: 2, flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }
            : undefined
        }
      >
      {/* Split view: PDF left, form right on desktop; PDF stacked above the form on mobile */}
      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Invoice PDF */}
        <Grid item xs={12} md={6}>
          {primaryAttachment ? (
            isMobile ? (
              <Box sx={{ height: "40vh" }}>
                <InvoicePdfViewer
                  url={primaryAttachment.url}
                  fileName={primaryAttachment.name}
                  fillHeight
                />
              </Box>
            ) : (
              <InvoicePdfViewer
                url={primaryAttachment.url}
                fileName={primaryAttachment.name}
                fillHeight
              />
            )
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height={isMobile ? "20vh" : "60vh"}
              sx={{ bgcolor: V2_COLORS.bgMuted, borderRadius: "8px" }}
            >
              <Typography color="textSecondary">No invoice document available</Typography>
            </Box>
          )}
        </Grid>

        {/* Edit form */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Invoice fields
          </Typography>
          {allFieldsValid && (
            <Alert severity="info" sx={{ mb: 2 }}>
              All invoice fields are valid. No corrections needed.
            </Alert>
          )}
          <Box sx={{ bgcolor: "background.paper", borderRadius: "8px", p: { xs: 2, md: 3 } }}>
            <EditableField
              label="Invoice Number"
              name="invoice_no"
              value={values.invoice_no}
              onChange={handleChange}
              onBlur={() => validateOnBlur("invoice_no")}
              required
              validation={getValidationStatus("invoice_no")}
              errorMessage={getErrorMessage("invoice_no")}
            />
            <EditableField
              label="Invoice Date"
              name="invoice_date"
              value={values.invoice_date}
              onChange={handleChange}
              onBlur={() => validateOnBlur("invoice_date")}
              type="date"
              required
              validation={getValidationStatus("invoice_date")}
              errorMessage={getErrorMessage("invoice_date")}
            />
            <EditableField
              label="Invoice Value"
              name="invoice_value"
              value={values.invoice_value}
              onChange={handleChange}
              onBlur={() => validateOnBlur("invoice_value")}
              type="number"
              required
              disabled={getBackendValidationStatus("invoice_value") === true}
              validation={getValidationStatus("invoice_value")}
              errorMessage={getErrorMessage("invoice_value")}
            />
            <EditableField
              label="Billing Entity"
              name="billing_entity"
              value={values.billing_entity}
              onChange={handleChange}
              onBlur={() => validateOnBlur("billing_entity")}
              disabled={getBackendValidationStatus("billing_entity") === true}
              validation={getValidationStatus("billing_entity")}
              errorMessage={getErrorMessage("billing_entity")}
            />
            <EditableField
              label="Bill to Address"
              name="bill_to_address"
              value={values.bill_to_address}
              onChange={handleChange}
              onBlur={() => validateOnBlur("bill_to_address")}
              type="textarea"
              disabled={getBackendValidationStatus("bill_to_address") === true}
              validation={getValidationStatus("bill_to_address")}
              errorMessage={getErrorMessage("bill_to_address")}
            />
            <EditableField
              label="Ship to Address"
              name="ship_to_address"
              value={values.ship_to_address}
              onChange={handleChange}
              onBlur={() => validateOnBlur("ship_to_address")}
              type="textarea"
              disabled={getBackendValidationStatus("ship_to_address") === true}
              validation={getValidationStatus("ship_to_address")}
              errorMessage={getErrorMessage("ship_to_address")}
            />
            <EditableField
              label="HSN / SAC Codes"
              name="hsn_sac_codes"
              value={values.hsn_sac_codes}
              onChange={handleChange}
              onBlur={() => validateOnBlur("hsn_sac_codes")}
              disabled={getBackendValidationStatus("hsn_sac_codes") === true}
              validation={getValidationStatus("hsn_sac_codes")}
              errorMessage={getErrorMessage("hsn_sac_codes")}
              helperText="Comma-separated codes"
            />
            <EditableField
              label="Additional Charges"
              name="additional_charges"
              value={values.additional_charges}
              onChange={handleChange}
              type="number"
              validation="IGNORE"
              helperText="Transportation, Packaging, Additional Work, etc"
            />

            {/* Comments (required for edit) */}
            <Box mt={3}>
              <Typography variant="body2" fontWeight={500} color="textSecondary" mb={1}>
                Comments <span style={{ color: V2_COLORS.error }}>*</span>
              </Typography>
              <TextField
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Describe the changes made..."
                fullWidth
                multiline
                minRows={2}
                size="small"
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
      </Box>

      {/* Cancel / Save: pinned bar on mobile, fixed bottom bar on desktop */}
      {isMobile ? (
        <StickyActionBar>{actionButtons}</StickyActionBar>
      ) : (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: "background.paper",
            px: 4,
            py: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            boxShadow: "0px -2px 8px rgba(0, 0, 0, 0.1)",
            zIndex: 10,
          }}
        >
          {actionButtons}
        </Box>
      )}
    </Box>
  );
}
